/**
 * Theme state — dark by default, manual toggle, persisted to localStorage.
 * Per docs/plans/2026-04-28-frontend-redesign.md §10 decision: dark by
 * default; honor `prefers-color-scheme` only when the operator has not
 * chosen explicitly.
 *
 * Phase 5 of the redesign.
 */

import { create } from 'zustand'

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'aegis-theme'

function detectInitial(): Theme {
  if (typeof window === 'undefined') return 'dark'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  // Operator hasn't chosen — honor the system preference.
  if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light'
  return 'dark'
}

function applyToDocument(theme: Theme): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', theme)
}

interface ThemeState {
  theme: Theme
  toggle: () => void
  set: (t: Theme) => void
}

export const useThemeStore = create<ThemeState>((set) => ({
  theme: detectInitial(),
  toggle: () =>
    set((s) => {
      const next: Theme = s.theme === 'dark' ? 'light' : 'dark'
      applyToDocument(next)
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // localStorage may be unavailable (private browsing, e.g.) — UI still flips.
      }
      return { theme: next }
    }),
  set: (theme) =>
    set(() => {
      applyToDocument(theme)
      try {
        window.localStorage.setItem(STORAGE_KEY, theme)
      } catch {
        // ignore
      }
      return { theme }
    }),
}))

// Apply the initial theme to the document on module load so there's no flash.
applyToDocument(detectInitial())
